import qs from "qs"
import { useRouter } from "next/router"
import React, { ReactNode } from "react"
import { ParsedUrlQuery } from "querystring"
import dayjs, { ConfigType, Dayjs, OpUnitType } from "dayjs"
import isPlainObject from "is-plain-object"
import { Breakpoints } from "~/constants/breakpoints"
import clone from "rfdc/default"
import {
  BizWorkflowValue,
  Document,
  DocumentType,
  FileObject,
  SignData,
  WorkflowActionId,
  WorkflowActor,
} from "~/model/Workflow"
import { Contract, ContractTypeValues } from "~/model/Contract"
import { DATE_API, DATE_TIME_DISPLAY } from "~/constants/date"
import utc from "dayjs/plugin/utc"
import { KEY_HANDOVER_DOCUMENT_1, KEY_HANDOVER_DOCUMENT_2, KEY_HANDOVER_DOCUMENT_3, KEY_HANDOVER_GUIDANCE } from "~/constants/multiupload"
import { DocWorkflowNos } from "~/model/ContractDocument"
dayjs.extend(utc)

// @ts-ignore
export const Month = () =>
  Array.from(Array(12).keys()).map((n) => ({ label: `${n + 1}ヶ月`, value: n + 1 }))

// @ts-ignore
export const Month48 = () =>
  Array.from(Array(48).keys()).map((n) => ({ label: `${n + 1}ヶ月`, value: n + 1 }))

export const yearOfBirth = () => {
  const currentYear = dayjs().get("year")
  return Array.from(Array(currentYear - 1899).keys()).map((item) => ({
    label: item + 1900,
    value: item + 1900,
  }))
}

export const monthOfBirth = () =>
  Array.from(Array(12).keys()).map((item) => ({ label: item + 1, value: item + 1 }))

export const dayOfBirth = (month: string) => {
  const listDay = dayjs(month).daysInMonth()
  return Array.from(Array(listDay).keys()).map((item) => ({ label: item + 1, value: item + 1 }))
}

export function valueToLabel<T>(
  data: T[],
  label: string,
  value: string
): { label: any; value: any }[] {
  return data.map((item) => ({ label: item[label], value: item[value] }))
}

export function format(n: number, isOffset: boolean = true) {
  const offset = isOffset ? 100 : 1
  return (Math.round(n / offset) * offset).toLocaleString("ja-JP", {})
}

export function simpleFormat(n: number) {
  return n.toLocaleString("ja-JP", {})
}

export function sum(prices: number[]) {
  return prices.length === 0 ? 0 : prices.reduce((accumulator, curr) => accumulator + curr)
}

export function kv<T>(data: T[], key = "id"): { [P in string]: T } {
  return Object.fromEntries(data.map((item) => [item[key], item]))
}

export function mergeEntries<T>(data: [string, T][]): { [P in string]: T[] } {
  const newDataSource = {}
  data.forEach((item) => {
    const target = item[0]
    if (!newDataSource[target]) {
      newDataSource[target] = []
    }
    newDataSource[target].push(item[1])
  })
  return newDataSource
}

export function mergeSum<T>(data: T[], sumKey: string, mergeKey: string = "id"): T[] {
  const newDataSource = {}
  data.forEach((item) => {
    const target = item[mergeKey]
    if (newDataSource[target]) {
      newDataSource[target] = { ...item, [sumKey]: newDataSource[target][sumKey] + item[sumKey] }
    } else {
      newDataSource[target] = item
    }
  })
  return Object.values(newDataSource)
}

export function deepCopy<T>(data: T): T {
  return JSON.parse(JSON.stringify(data))
}

export const toHalfWidth = (str: string) =>
  str.replace(/[０-９]/g, (tmpStr) => {
    return String.fromCharCode(tmpStr.charCodeAt(0) - 0xfee0)
  })

export function unique<T>(data: T[], key: string = "id"): T[] {
  return Object.values(
    kv(
      data.filter((item) => item),
      key
    )
  )
}

export function activeLink(path: string): string {
  const className = "active"
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const router = useRouter()
  if (path === "/" || path === "") {
    return router.route === "/" ? className : ""
  } else {
    const regex = new RegExp(`^${path}`)
    return regex.test(router.route) ? className : ""
  }
}

export function nl2br(text: string | string[]): ReactNode {
  return (Array.isArray(text) ? text.join("\n") : text || "").split(/(\n)/).map((item, index) => {
    return (
      <React.Fragment key={index}>
        {item.match(/\n/) ? <br /> : item.replace(",", "")}
      </React.Fragment>
    )
  })
}

export function isAfterToday(date: string) {
  return Boolean(dayjs(date).isAfter(dayjs().toString()))
}

export function paramasToQuery(params: object, emptySkip: boolean = false): ParsedUrlQuery {
  const queryString = qs.stringify(params, { arrayFormat: "repeat" })
  const queryParams = new URLSearchParams(queryString)
  const query: { [KEY in string]: string } = {}
  queryParams.forEach((value, key) => {
    query[key] = value
  })

  if (emptySkip) {
    Object.keys(query).forEach((key) => {
      if (query[key] === "" || query[key] === undefined || query[key] === null) {
        delete query[key]
      }
    })
  }
  return query
}

export function objectFilter(params: object, keys: string[]): object {
  const queryString = qs.stringify(params, { arrayFormat: "repeat" })
  const queryParams = qs.parse(queryString) as { [KEY in string]: any }
  return Object.fromEntries(
    keys
      .map((key) => [key, queryParams[key]])
      .filter((key) => !(queryParams[key[0]] === undefined || queryParams[key[0]] === null))
  )
}

export function paramsSerializer(params: object): string {
  return qs.stringify(params)
}

export const isMobile = (() => {
  if (typeof navigator === "undefined" || typeof navigator.userAgent !== "string") {
    return false
  }
  return /Mobile/.test(navigator.userAgent)
})()

// Recursively removes any object keys with a value of undefined
export function removeUndefineds<T>(obj: T): T {
  if (!isPlainObject(obj)) return obj

  const target: { [name: string]: any } = {}

  for (const key in obj) {
    const val = obj[key]
    if (typeof val !== "undefined") {
      target[key] = removeUndefineds(val)
    }
  }

  return target as T
}

export async function getDeviceInfo() {
  const devices = await navigator.mediaDevices.enumerateDevices()

  return {
    audioInputDevices: devices.filter((device) => device.kind === "audioinput"),
    videoInputDevices: devices.filter((device) => device.kind === "videoinput"),
    audioOutputDevices: devices.filter((device) => device.kind === "audiooutput"),
    hasAudioInputDevices: devices.some((device) => device.kind === "audioinput"),
    hasVideoInputDevices: devices.some((device) => device.kind === "videoinput"),
  }
}

// This function will return 'true' when the specified permission has been denied by the user.
// If the API doesn't exist, or the query function returns an error, 'false' will be returned.
export async function isPermissionDenied(name: any) {
  if (navigator.permissions) {
    try {
      const result = await navigator.permissions.query({ name })
      return result.state === "denied"
    } catch {
      return false
    }
  } else {
    return false
  }
}

export function breakpoint(breakpoint: Breakpoints) {
  return "@media only screen and (max-width: 600px)"
  //return createBreakpoint(breakpoints)(breakpoint);
}

export function replaceCellProps(
  values: any[],
  objectId: string,
  propKey: string,
  replaceData: any
) {
  const data = clone(values)
  data.forEach((item1) => {
    item1.forEach((item2) => {
      item2.contents.forEach((item3) => {
        if (item3.objectId === objectId) {
          item3.data[propKey] = replaceData
        }
      })
    })
  })
  return data
}

export function getCellProps(data: any[], objectId: string, propKey: string) {
  let result = null
  data.forEach((item1) => {
    item1.forEach((item2) => {
      item2.contents.forEach((item3) => {
        if (item3.objectId === objectId) {
          result = item3.data[propKey]
        }
      })
    })
  })
  return result
}

export function checkIsNull(data: any) {
  if (data === null || data === undefined || data === "") {
    return true
  }
  return false
}

export const renderOptionNum = () => {
  const arrNum = []
  for (let i = 1; i <= 30; i++) {
    arrNum.push(i)
  }
  return [
    { label: "更新回数を選択", value: "" },
    ...arrNum.map((item) => ({ label: `(${item})`, value: item })),
  ]
}

export const checkStringJson = (json: string) => {
  try {
    const data = JSON.parse(json)
    return data
  } catch (e) {
    return false
  }
}

export const isEmptyObj = (obj: Object) => {
  return Object.keys(obj).length === 0
}

export const autoDownloadFileByLink = (link: any, fileName?: string) => {
  const linkTag = document.createElement("a")
  linkTag.href = link
  linkTag.setAttribute("id", "download-file")
  if (fileName) {
    linkTag.download = fileName
  }
  document.body.appendChild(linkTag)
  linkTag.click()
  const downloadFile = document.getElementById("download-file")
  downloadFile.remove()
}

export const getHashDocument = (
  document,
  isReceivedDoc = false,
  isDownloadMode = false,
  isModeView = false
) => {
  let hash = ""
  const step = document.workflowData.findLast((step) => step.result?.hash && !step.result?.isSkip)
  hash = step?.result?.hash
  return hash
}

export const getDocumentNameAndId = (documents: Document[]) => {
  const result = []
  documents.forEach(({ workflowData, workflowName }) => {
    const workflowDataReverse = structuredClone(workflowData).reverse()
    const isSignType = workflowDataReverse.find(
      (e) => e.type === WorkflowActionId.SIGN && e.result && !e.result?.isSkip
    )
    const isUploadType = workflowDataReverse.find(
      (e) => e.type === WorkflowActionId.UPLOAD && e.result
    )
    if (isSignType) {
      result.push({
        id: (isSignType.result?.data as SignData)?.signedPdfId || "",
        name: workflowName,
      })
    } else {
      result.push({
        id: (isUploadType.result?.data as FileObject)?.fileId || "",
        name: workflowName,
      })
    }
  })
  return result
}

export function toASCII(chars: string) {
  let ascii = ""
  for (let i = 0, l = chars.length; i < l; i++) {
    let c = chars[i].charCodeAt(0)

    if (c >= 0xff00 && c <= 0xffef) {
      c = 0xff & (c + 0x20)
    }

    ascii += String.fromCharCode(c)
  }

  return ascii
}

export const shortenDocumentPdfId = (str: string) => {
  const documentIdLength = str.length
  const preStr = str.substring(0, 4)
  const sufStr = str.substring(documentIdLength - 4, documentIdLength + 1)
  return preStr + "..." + sufStr
}

export const isTypeShare = (document) => {
  return (
    (document?.workflowType === DocumentType.SHARE ||
      document?.workflowType === DocumentType.INVOICE) &&
    document?.workflowData?.length < 5
  )
}

export const getWorkflowType = (document) => {
  return document.workflowData[document.workflowPosition || 0]?.type
}

export const isTypeInvoiceWf5 = (document) => {
  return document?.workflowType === DocumentType.INVOICE && document?.workflowData?.length < 5
}

/**
 * Bracket types that can wrap the document name in a file name
 * (half-width [] and full-width 【】)
 */
const FILE_NAME_BRACKET_PAIRS: Record<string, string> = {
  "[": "]",
  "【": "】",
}

const FILE_NAME_OPEN_BRACKET_REGEX = new RegExp(`[${Object.keys(FILE_NAME_BRACKET_PAIRS).join("")}]`)

/**
 * Extract the document name inside the first brackets found in a file name
 * e.g. "00000 恵比寿ガーデンプレイス 101 【賃貸借契約書】.pdf" -> "賃貸借契約書"
 */
const extractBracketLabel = (fileName?: string): string => {
  const openMatch = fileName?.match(FILE_NAME_OPEN_BRACKET_REGEX)
  if (!openMatch) return ""

  const open = openMatch[0]
  const close = FILE_NAME_BRACKET_PAIRS[open]
  const openIndex = openMatch.index as number
  const closeIndex = fileName.indexOf(close, openIndex + open.length)
  if (closeIndex < 0) return ""

  return fileName.slice(openIndex + open.length, closeIndex).trim()
}

/**
 * Find the workflow matching the [document name] at the beginning of a file name
 * e.g. "[使用細則] abcxyz.pdf" -> returns the workflow whose label is "使用細則"
 * e.g. "【賃貸借契約書】 00000 恵比寿ガーデンプレイス 101.pdf" -> returns the workflow whose label is "賃貸借契約書"
 */
export const matchWorkflowByFileName = <T extends { label: string, docWorkflowNo?: DocWorkflowNos, viewType: number }>(
  fileName: string,
  workflowDataList: T[]
): T | null => {
  const bracketLabel = extractBracketLabel(fileName)
  if (!bracketLabel) return null

  const excludedWorkflowNos = [
    DocWorkflowNos.DOC_WORKFLOW_20,
    DocWorkflowNos.DOC_WORKFLOW_99,
    DocWorkflowNos.DOC_WORKFLOW_05,
  ]

  if ([KEY_HANDOVER_DOCUMENT_1, KEY_HANDOVER_DOCUMENT_2, KEY_HANDOVER_DOCUMENT_3].includes(bracketLabel)) {
    return workflowDataList.find((item) => item.label.trim() === KEY_HANDOVER_GUIDANCE
      && !excludedWorkflowNos.includes(item.docWorkflowNo) && item.viewType !== 0)
      || null
  }

  return workflowDataList.find((item) => item.label.trim() === bracketLabel
    && !excludedWorkflowNos.includes(item.docWorkflowNo) && item.viewType !== 0)
    || null
}

export const checkValidateUpload = (
  files: FileList,
  value,
  showDialog,
  onSuccess,
  maxFile = 10
) => {
  const fileList = Array.from(files)
  const totalFile = value ? [...fileList, ...value] : [...fileList]

  if (totalFile?.length > maxFile) {
    showDialog("エラー", `${maxFile}ファイル以上を選択している`, "閉じる")
  } else {
    const maxSize = 30 * 1024 * 1024
    const oversizedFiles = fileList?.filter((file) => file.size > maxSize)
    const validFiles = fileList?.filter((element) => !oversizedFiles?.includes(element))
    // 正常にアップロードできたファイル：${validFiles.map((file) => file.name).join("、")}
    if (oversizedFiles.length > 0) {
      if (oversizedFiles.length === 1 && validFiles.length === 0) {
        showDialog("エラー", "ファイルサイズ30MBを超えている", "閉じる")
      } else {
        showDialog(
          "エラー",
          `ファイルサイズが30MBを超えているファイルがあるため、アップロードすることができませんでした。
          アップロード失敗したファイル：${oversizedFiles.map((file) => file.name).join("、")}`,
          "閉じる"
        ).then(() => onSuccess(validFiles))
      }
    } else {
      onSuccess(files)
    }
  }
}

export const checkIsFilePdf = (files: FileList, accept: string) => {
  const fileList = Array.from(files)
  const result = fileList.filter((file) => {
    return !(accept === "*.*" || accept === file.type)
  })?.length
  return result
}

export const formatDateFilter = (date, isEnd = false) => {
  const dateFormat = dayjs(date)
  return isEnd
    ? dateFormat.endOf("day").format("YYYY-MM-DDTHH:mm:ss")
    : dateFormat.format("YYYY-MM-DDTHH:mm:ss")
}

export const formatPrice = (price) => {
  const numberFormat = Number(price)
  return isNaN(numberFormat) ? 0 : new Intl.NumberFormat("ja-JP").format(numberFormat)
}

export const removeIfIsBusiness = (contractorType: string, value) => {
  return contractorType === "法人" ? null : value
}

export const getBusinessCompanyName = (contract: Contract) => {
  if (contract.typeContractor === "法人") {
    return contract?.businessCompany?.name || contract.businessCompanyName
  }
  return contract.name1
}

export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export const formatDate = (value: ConfigType, formatType = DATE_API) => {
  return value && dayjs(value).isValid() ? dayjs(value).format(formatType) : ""
}

export const extractFilename = (inputString: string) => {
  const match = inputString.match(/filename=([^;]+)/)
  return match ? match[1] : ""
}

export const downloadFileCsv = (url: string, fileName: string) => {
  const link = document.createElement("a")
  link.href = url
  link.download = extractFilename(fileName)
  document.body.appendChild(link)
  link.click()
  link.parentNode?.removeChild(link)
}

export const compareDates = {
  greater(dateParam1: Dayjs, dateParam2: Dayjs, type: OpUnitType = "day"): boolean {
    const date1 = dayjs(dateParam1, { format: "YYYYMMDD" })
    const date2 = dayjs(dateParam2, { format: "YYYYMMDD" })
    return date1.isAfter(date2, type)
  },

  lessThan(dateParam1: Dayjs, dateParam2: Dayjs, type: OpUnitType = "day"): boolean {
    const date1 = dayjs(dateParam1, { format: "YYYYMMDD" })
    const date2 = dayjs(dateParam2, { format: "YYYYMMDD" })
    return date1.isBefore(date2, type)
  },

  same(dateParam1: Dayjs, dateParam2: Dayjs, type: OpUnitType = "day"): boolean {
    const date1 = dayjs(dateParam1, { format: "YYYYMMDD" })
    const date2 = dayjs(dateParam2, { format: "YYYYMMDD" })
    return date1.isSame(date2, type)
  },
}

export const getDateFilterUTC = (startDate, endDate) => {
  const start = dayjs(startDate).utc().format(DATE_TIME_DISPLAY)
  const end = dayjs(endDate).endOf("day").utc().format(DATE_TIME_DISPLAY)
  return { start, end }
}

export function getPriorityWorkflowShareSortable(workflowName: string): number {
  const LOW_PRIORITY = 9999

  // Priority document workflow type SHARE is arranged from 1 to 9
  const mappingPrioritySortableShareContract = {
    請求書: 1,
    お客様控え書類: 2,
    火災保険資料: 3,
    ハザードマップ: 4,
    平面図: 5,
  } as const

  for (const [key, value] of Object.entries(mappingPrioritySortableShareContract)) {
    if (workflowName.trim() === key) return value - 0.5
    if (workflowName.trim().startsWith(key)) return value
  }
  return LOW_PRIORITY
}

export function checkEmptyObjValues(obj: Record<string, any>) {
  return Object.values(obj).some(
    (value) => value === null || value === undefined || value.trim() === ""
  )
}
export const calculateBizWorkflow = (contract: Contract) => {
  const hasGuarantee = contract.flagGuarantee || contract.flagGuarantee2
  const isSublease = contract.contractType === ContractTypeValues.SUBLEASE
  const isOwnerAgency = contract.contractType === ContractTypeValues.BROKERAGE
  const isMasterLease2 = contract.contractType === ContractTypeValues.MASTER_LEASE_2
  if (isSublease) {
    if (contract.businessCompanyId) {
      return hasGuarantee ? BizWorkflowValue.BIZ_4 : BizWorkflowValue.BIZ_3
    } else {
      return hasGuarantee ? BizWorkflowValue.BIZ_2 : BizWorkflowValue.BIZ_1
    }
  } else if (isOwnerAgency) {
    if (contract.businessCompanyId) {
      return hasGuarantee ? BizWorkflowValue.BIZ_8 : BizWorkflowValue.BIZ_7
    } else {
      return hasGuarantee ? BizWorkflowValue.BIZ_6 : BizWorkflowValue.BIZ_5
    }
  } else if (isMasterLease2) {
    if (contract.businessCompanyId) {
      return hasGuarantee ? BizWorkflowValue.BIZ_16 : BizWorkflowValue.BIZ_15
    }
    return hasGuarantee ? BizWorkflowValue.BIZ_14 : BizWorkflowValue.BIZ_13
  } else {
    if (contract.businessCompanyId) {
      return hasGuarantee ? BizWorkflowValue.BIZ_12 : BizWorkflowValue.BIZ_11
    } else {
      return hasGuarantee ? BizWorkflowValue.BIZ_10 : BizWorkflowValue.BIZ_9
    }
  }
}
